/* @flow */
import PropTypes from 'prop-types';
import * as React from 'react';

import AddButton from 'components/AddButton';
import Columns2 from 'components/Columns2';
import ErrorReporter from 'app/components/ErrorReporter';
import Fieldset from 'components/Fieldset';
import Glyph from 'components/Glyph';
import Legend from 'components/Legend';
import LoadingIndicator from 'components/LoadingIndicator';
import Organization from 'entities/Organization';
import SchoolPicker from 'components/SchoolPicker';
import ToastActions from 'actions/ToastActions';

import SettingsContainer from 'settings/components/SettingsContainer';
import SettingsSchoolsListItem from 'settings/components/SettingsSchoolsListItem';

import './styles/UserSchoolsForm.scss';

type Props = {
  updateUserOrgs: (orgIds: number[]) => Promise<void>,
  organizations: *,
  classes: *,
  loading: boolean,
};

type State = {
  isEditing: boolean,
  addingId: ?Backbone$Id,
};

class UserSchoolsForm extends React.Component<Props, State> {
  state: State = {
    isEditing: false,
    addingId: null,
  };

  static childContextTypes = {
    eventScreenName: PropTypes.string,
  };

  getChildContext() {
    return {
      eventScreenName: 'SettingsSchool',
    };
  }

  componentDidUpdate(prevProps: Object, prevState: State) {
    if (this.state.isEditing && !prevState.isEditing) {
      this.refs.schoolPicker.focus();
    }
  }

  handleSelectSchool = (schoolId: Backbone$Id) => {
    const { organizations } = this.props;

    this.setState({ isEditing: false, addingId: schoolId });
    // updateUserOrgs requires number[]
    const schoolIds = organizations
      .map(o => Number(o && o.id))
      .filter(id => typeof id === 'number');
    schoolIds.push(Number(schoolId));
    this.props.updateUserOrgs(schoolIds).then(this.handleAddSuccess, this.handleAddError);
  };

  handleAddSuccess = () => {
    this.setState({ addingId: null });
  };

  handleAddError = () => {
    ToastActions.showDefaultErrorToast();
    this.setState({ addingId: null });
  };

  filterSearchResults = (orgs: Array<Organization>): Array<Organization> => {
    const { organizations } = this.props;

    return orgs.filter(org => {
      return !organizations.some(s => s && s.id === org.id);
    });
  };

  renderAddSchool = () => {
    const addSchool = this.state.isEditing ? (
      <SchoolPicker
        className="UserSchoolsForm-schoolPicker"
        organizationType="School"
        filterSearchResults={this.filterSearchResults}
        onSelect={this.handleSelectSchool}
        onClear={() => {}}
        onCreateOrganization={school => {
          this.handleSelectSchool(school.id);
        }}
        onBlur={() => {
          this.setState({ isEditing: false });
        }}
        noQuery="Search for a school"
        ref="schoolPicker"
      />
    ) : (
      <AddButton
        size="large"
        onClick={() => {
          this.setState({ isEditing: true });
        }}>
        Join a school
      </AddButton>
    );

    return addSchool;
  };

  renderSchoolRow = (submitting: boolean, school: *) => {
    const { classes } = this.props;

    if (!school) {
      return <ErrorReporter error="UserSchoolsForm - tried to render school row without school" />;
    }

    return (
      <SettingsSchoolsListItem
        key={school.id}
        school={school}
        affiliation={school.userAffiliation}
        groups={
          classes
            ? classes.filter(
                c => c && c.organization && c.organization.id === school.id && !c.staffClassOrgId
              )
            : []
        }
        staffClass={classes ? classes.find(c => c && c.staffClassOrgId === school.id) : null}
        submitting={submitting}
      />
    );
  };

  render() {
    const { loading, organizations } = this.props;

    if (loading) {
      return <LoadingIndicator />;
    }

    if (!loading && !organizations) {
      return <ErrorReporter error="UserSchoolsForm - failed to load data" />;
    }

    const { addingId } = this.state;

    return (
      <div className="UserSchoolsForm">
        <Fieldset>
          <Legend>{organizations.length > 1 ? 'Organizations' : 'Organization'}</Legend>
          <SettingsContainer>
            <div className="UserSchoolsForm-schoolList">
              {organizations.map(org => org && this.renderSchoolRow(false, org))}
            </div>
            <Columns2>
              <div className="UserSchoolsForm-row">
                {addingId ? <LoadingIndicator /> : this.renderAddSchool()}
              </div>
              {this.state.isEditing && (
                <Glyph className="UserSchoolsForm-closeButton" name="close" size="large" />
              )}
            </Columns2>
          </SettingsContainer>
        </Fieldset>
      </div>
    );
  }
}

export default UserSchoolsForm;
